import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, createVNode as _createVNode, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "app-file-preview__loading"
}
const _hoisted_2 = {
  key: 1,
  class: "app-file-preview__error"
}
const _hoisted_3 = {
  key: 0,
  class: "app-file-preview__image"
}
const _hoisted_4 = ["src", "alt"]
const _hoisted_5 = { class: "app-file-preview__zoom-instructions" }
const _hoisted_6 = {
  key: 1,
  class: "app-file-preview__pdf"
}
const _hoisted_7 = {
  key: 2,
  class: "app-file-preview__text"
}
const _hoisted_8 = {
  key: 3,
  class: "app-file-preview__unavailable"
}
const _hoisted_9 = { class: "app-file-preview__icon" }

import { ref, computed } from 'vue';
// @ts-ignore
import { watch, onMounted, onUnmounted, nextTick } from 'vue';
import mediumZoom from 'medium-zoom';
import SimplePDFViewer from './SimplePDFViewer.vue';

interface Props {
  fileUrl: string;
  fileName: string;
  fileType: string;
  loading: boolean;
  error: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'FilePreview',
  props: {
    fileUrl: { default: '' },
    fileName: { default: '' },
    fileType: { default: '' },
    loading: { type: Boolean, default: false },
    error: { default: '' }
  },
  setup(__props: any) {

/// <reference types="vue" />
// @ts-ignore
const props = __props;

const textContent = ref<string>('');
const imageZoom = ref<any>(null);

const fileExtension = computed(() => {
  if (props.fileType) return props.fileType;
  return props.fileName ? props.fileName.split('.').pop()?.toLowerCase() : '';
});

const isImage = computed(() => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
  return imageExtensions.includes(fileExtension.value);
});

const isPdf = computed(() => {
  return fileExtension.value === 'pdf';
});

const isTextFile = computed(() => {
  const textExtensions = ['txt', 'md', 'json', 'xml', 'csv', 'html', 'css', 'js'];
  return textExtensions.includes(fileExtension.value);
});

// Load text content if it's a text file
watch(() => [props.fileUrl, isTextFile.value], async ([url, isText]: [string, boolean]) => {
  if (url && isText) {
    try {
      const response = await fetch(url);
      textContent.value = await response.text();
    } catch (error) {
      textContent.value = '';
      console.error('Failed to load text content:', error);
    }
  }
}, { immediate: true });

// Initialize zoom for images
onMounted(() => {
  if (isImage.value && props.fileUrl) {
    nextTick(() => {
      const imgElement = document.querySelector('.image-preview img') as HTMLImageElement | null;
      if (imgElement) {
        imageZoom.value = mediumZoom(imgElement, {
          background: 'rgba(0, 0, 0, 0.8)',
          margin: 20
        });
      }
    });
  }
});

// Clean up zoom instance
onUnmounted(() => {
  if (imageZoom.value) {
    imageZoom.value.detach();
  }
});

const onImageLoad = (): void => {
  console.log('Image loaded successfully');
};

const onImageError = (error: Event): void => {
  console.error('Failed to load image:', error);
};

// Watch for URL changes to reinitialize zoom
watch(() => props.fileUrl, (newUrl: string) => {
  if (newUrl && isImage.value) {
    nextTick(() => {
      if (imageZoom.value) {
        imageZoom.value.detach();
      }
      const imgElement = document.querySelector('.image-preview img') as HTMLImageElement | null;
      if (imgElement) {
        imageZoom.value = mediumZoom(imgElement, {
          background: 'rgba(0, 0, 0, 0.8)',
          margin: 20
        });
      }
    });
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["app-file-preview", { 'app-file-preview--loading': _ctx.loading }])
  }, [
    (_ctx.loading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _cache[0] || (_cache[0] = _createElementVNode("div", { class: "app-loading-spinner" }, null, -1)),
          _createElementVNode("p", null, _toDisplayString(_ctx.$t('common.loading')), 1)
        ]))
      : (_ctx.error)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _cache[1] || (_cache[1] = _createElementVNode("i", { class: "fas fa-exclamation-triangle" }, null, -1)),
            _createElementVNode("p", null, _toDisplayString(_ctx.error), 1),
            _renderSlot(_ctx.$slots, "error-actions")
          ]))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
            (isImage.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createElementVNode("img", {
                    src: _ctx.fileUrl,
                    alt: _ctx.fileName || '',
                    class: "app-file-preview__zoomable",
                    onLoad: onImageLoad,
                    onError: onImageError
                  }, null, 40, _hoisted_4),
                  _createElementVNode("div", _hoisted_5, [
                    _cache[2] || (_cache[2] = _createElementVNode("i", { class: "fas fa-search-plus" }, null, -1)),
                    _createElementVNode("span", null, _toDisplayString(_ctx.$t('documents.clickToZoom')), 1)
                  ])
                ]))
              : (isPdf.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    _createVNode(SimplePDFViewer, { pdfUrl: _ctx.fileUrl }, null, 8, ["pdfUrl"])
                  ]))
                : (isTextFile.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createElementVNode("pre", null, _toDisplayString(textContent.value), 1)
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_8, [
                      _createElementVNode("div", _hoisted_9, [
                        _renderSlot(_ctx.$slots, "file-icon", {}, () => [
                          _cache[3] || (_cache[3] = _createElementVNode("i", {
                            class: "fas fa-file",
                            style: {"font-size":"3rem","color":"#95a5a6"}
                          }, null, -1))
                        ])
                      ]),
                      _createElementVNode("p", null, _toDisplayString(_ctx.$t('documents.noPreviewAvailable')), 1),
                      _renderSlot(_ctx.$slots, "no-preview-actions")
                    ]))
          ], 64))
  ], 2))
}
}

})